import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { bookingApi, unwrap, type ApiEnvelope } from '@/lib/api/client';
import type { EventHost, EventLocation, EventServiceType } from '@/features/events/types';
import type { BookingDetail, PagedAdminBookings } from './types';

const get = <T>(url: string) => unwrap<T>(bookingApi.get<ApiEnvelope<T>>(url));

/**
 * `AdminBookingSearchModel` — the one payload behind search, the dashboard and
 * the export, which is why it is built in a single place.
 *
 * Every filter is nullable and "any" means omitted, not a sentinel.
 */
export interface BookingSearchModel {
  startDate: string;
  endDate: string;
  locationId: string | null;
  eventId: string | null;
  serviceTypeId: string | null;
  hostId: string | null;
  eventType: number | null;
  paymentCategory: number | null;
  status: number | null;
  checkedIn: boolean | null;
  query: string;
  page: number;
  size: number;
}

export function useBookingSearch(model: BookingSearchModel, enabled = true) {
  return useQuery({
    queryKey: ['manage-bookings', 'search', model],
    queryFn: () =>
      unwrap<PagedAdminBookings>(
        bookingApi.post('v2/admin/bookings/search', model)
      ),
    enabled,
    placeholderData: keepPreviousData,
  });
}

/** One booking, with its purchases, payments and move history. */
export function useBookingDetail(bookingId: string | null) {
  return useQuery({
    queryKey: ['manage-bookings', bookingId],
    queryFn: () => get<BookingDetail>(`v2/admin/bookings/${bookingId}`),
    enabled: Boolean(bookingId),
  });
}

/** `BookingExportRowDto[]` — a JSON array the client turns into a CSV. */
export function useExportBookings() {
  return useMutation({
    mutationFn: (model: BookingSearchModel) =>
      unwrap<Record<string, unknown>[]>(
        bookingApi.post('v2/admin/bookings/export', model)
      ),
  });
}

/** Reverses a recorded payment. Admin-only in the original. */
export function useVoidPayment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (paymentId: string) =>
      unwrap<unknown>(
        bookingApi.delete(`v2/admin/bookings/payments/${paymentId}`)
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['manage-bookings'] });
    },
  });
}

/**
 * Cancels a booking — `POST events/booking/{id}` with a `CancelBookingModel`.
 *
 * The server refunds online payments less `cancellationFee`, releases the
 * spot and emails the customer. Sent with the same configured fee the event
 * time-slot sheet uses, so both ways of cancelling charge the same.
 */
export function useCancelBooking() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      bookingId,
      cancellationFee,
    }: {
      bookingId: string;
      cancellationFee: number;
    }) =>
      unwrap<unknown>(
        bookingApi.post(`events/booking/${bookingId}`, { cancellationFee })
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['manage-bookings'] });
      // The event time-slot sheets cache their own booking lists.
      void queryClient.invalidateQueries({ queryKey: ['events'] });
    },
  });
}

// ---------------------------------------------------------- filter sources

export function useBookingLocations() {
  return useQuery({
    queryKey: ['booking', 'locations'],
    queryFn: () => get<EventLocation[]>('locations'),
    staleTime: 5 * 60 * 1000,
  });
}

export function useBookingServiceTypes() {
  return useQuery({
    queryKey: ['booking', 'service-types'],
    queryFn: () => get<EventServiceType[]>('servicetypes/list'),
    staleTime: 5 * 60 * 1000,
  });
}

export function useBookingHosts() {
  return useQuery({
    queryKey: ['booking', 'hosts'],
    queryFn: () => get<EventHost[]>('hosts'),
    staleTime: 5 * 60 * 1000,
  });
}
